import { useEffect, useRef, useState } from "react";
import { useNavigate, useParams, Link } from "react-router-dom";
import { api, mediaUrl } from "../../lib/api";
import { toast } from "sonner";
import {
  Save,
  Upload,
  Link as LinkIcon,
  Trash2,
  ChevronUp,
  ChevronDown,
  Type,
  Heading2,
  Quote,
  Image as ImageIcon,
  Video,
  Eye,
  ArrowLeft,
  Loader2,
} from "lucide-react";

const blank = () => ({
  title: "",
  subtitle: "",
  cover_image_url: "",
  blocks: [{ type: "paragraph", text: "" }],
  tags: [],
  status: "draft",
  is_featured: false,
  featured_order: null,
  author_display: "",
  teaser_label: "auto",
  published_at: "",
});

// Convert an ISO datetime coming from the API ("2026-09-29T09:00:00Z" or
// "…+00:00") into the `datetime-local` input format ("2026-09-29T09:00")
// expressed in the browser's local timezone so the editor shows what the
// visitor will see.
const isoToLocalInput = (iso) => {
  if (!iso) return "";
  const d = new Date(iso);
  if (Number.isNaN(d.getTime())) return "";
  const pad = (n) => String(n).padStart(2, "0");
  return (
    `${d.getFullYear()}-${pad(d.getMonth() + 1)}-${pad(d.getDate())}` +
    `T${pad(d.getHours())}:${pad(d.getMinutes())}`
  );
};

// Inverse: datetime-local (browser local TZ) → ISO UTC string persisted
// in the DB. Empty string means "no override" and gets serialized as null.
const localInputToIso = (local) => {
  if (!local) return "";
  const d = new Date(local);
  if (Number.isNaN(d.getTime())) return "";
  return d.toISOString();
};

export const AdminArticleEditor = () => {
  const { id } = useParams();
  const navigate = useNavigate();
  const isNew = !id || id === "nouveau";
  const [form, setForm] = useState(blank());
  const [loading, setLoading] = useState(!isNew);
  const [saving, setSaving] = useState(false);
  const [meta, setMeta] = useState(null); // slug, updated_at, etc. when editing

  useEffect(() => {
    if (isNew) return;
    api.get(`/articles/all`).then((r) => {
      const doc = r.data.find((a) => a.id === id);
      if (!doc) {
        toast.error("Article introuvable");
        navigate("/admin/articles");
        return;
      }
      setForm({
        title: doc.title,
        subtitle: doc.subtitle || "",
        cover_image_url: doc.cover_image_url || "",
        blocks: doc.blocks?.length ? doc.blocks : [{ type: "paragraph", text: "" }],
        tags: doc.tags || [],
        status: doc.status,
        is_featured: !!doc.is_featured,
        featured_order: Number.isFinite(doc.featured_order) ? doc.featured_order : null,
        author_display: doc.author_display || "",
        teaser_label: doc.teaser_label || "auto",
        published_at: isoToLocalInput(doc.published_at),
      });
      setMeta(doc);
    }).finally(() => setLoading(false));
  }, [id, isNew, navigate]);

  const update = (patch) => setForm((f) => ({ ...f, ...patch }));

  const addBlock = (type) => {
    const base = { type, text: "", url: "", caption: "" };
    setForm((f) => ({ ...f, blocks: [...f.blocks, base] }));
  };
  const updateBlock = (i, patch) => {
    setForm((f) => ({
      ...f,
      blocks: f.blocks.map((b, idx) => (idx === i ? { ...b, ...patch } : b)),
    }));
  };
  const removeBlock = (i) => {
    setForm((f) => ({ ...f, blocks: f.blocks.filter((_, idx) => idx !== i) }));
  };
  const moveBlock = (i, dir) => {
    setForm((f) => {
      const j = i + dir;
      if (j < 0 || j >= f.blocks.length) return f;
      const arr = [...f.blocks];
      [arr[i], arr[j]] = [arr[j], arr[i]];
      return { ...f, blocks: arr };
    });
  };

  const save = async (targetStatus) => {
    if (!form.title.trim()) {
      toast.error("Le titre est requis");
      return;
    }
    // Guard against the classic "empty media block" foot-gun: a user
    // adds a Vidéo/Photo block, forgets to actually attach the file (or
    // the upload silently timed out), then clicks Publier. The article
    // gets saved with `url:""`, and the public page shows a black
    // player with no source — indistinguishable from a broken video.
    // We refuse to save until every media block has either a stored
    // URL or an external (YouTube/Vimeo/…) link.
    const emptyMedia = form.blocks
      .map((b, i) => ({ ...b, idx: i + 1 }))
      .filter((b) => (b.type === "image" || b.type === "video") && !((b.url || "").trim()));
    if (emptyMedia.length > 0) {
      const first = emptyMedia[0];
      toast.error(
        `Le bloc ${first.type === "video" ? "Vidéo" : "Photo"} n°${first.idx} n'a pas de fichier attaché. Ré-uploadez le fichier ou supprimez le bloc avant de sauvegarder.`
      );
      return;
    }
    setSaving(true);
    // The `published_at` field in `form` stores a `datetime-local` value
    // in the browser's local TZ. Convert to ISO UTC for the API. Empty
    // string ⇒ let the backend decide (keep existing / auto-now on first
    // publish).
    const payload = {
      ...form,
      status: targetStatus || form.status,
      published_at: localInputToIso(form.published_at) || null,
    };
    try {
      if (isNew) {
        const { data } = await api.post("/articles", payload);
        toast.success(payload.status === "published" ? "Article publié" : "Brouillon enregistré");
        navigate(`/admin/articles/${data.id}`, { replace: true });
      } else {
        const { data } = await api.put(`/articles/${id}`, payload);
        setMeta(data);
        setForm((f) => ({ ...f, status: data.status }));
        toast.success(data.status === "published" ? "Article publié" : "Brouillon enregistré");
      }
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Enregistrement échoué");
    } finally {
      setSaving(false);
    }
  };

  if (loading) {
    return <div className="text-neutral-500 text-sm">Chargement…</div>;
  }

  return (
    <div className="space-y-8">
      <div className="flex items-start justify-between gap-4 flex-wrap">
        <div>
          <Link to="/admin/articles" className="inline-flex items-center gap-1 text-xs text-neutral-500 hover:text-[var(--red)]">
            <ArrowLeft className="h-3.5 w-3.5" /> Articles
          </Link>
          <h1 className="font-display text-3xl md:text-4xl mt-2">
            {isNew ? "Nouvel article" : "Modifier l'article"}
          </h1>
          {meta && (
            <div className="text-[11px] uppercase tracking-widest text-neutral-400 mt-1">
              Slug : /articles/{meta.slug}
            </div>
          )}
        </div>
        <div className="flex items-center gap-2">
          {meta?.status === "published" && (
            <Link
              to={`/articles/${meta.slug}`}
              target="_blank"
              rel="noreferrer"
              className="btn-ghost"
              data-testid="article-preview-btn"
            >
              <Eye className="h-4 w-4" /> Aperçu
            </Link>
          )}
          <button
            data-testid="article-save-draft"
            onClick={() => save("draft")}
            disabled={saving}
            className="btn-ghost"
          >
            {saving ? <Loader2 className="h-4 w-4 animate-spin" /> : <Save className="h-4 w-4" />}
            Enregistrer brouillon
          </button>
          <button
            data-testid="article-publish"
            onClick={() => save("published")}
            disabled={saving}
            className="btn-red"
          >
            {saving ? <Loader2 className="h-4 w-4 animate-spin" /> : <Save className="h-4 w-4" />}
            Publier
          </button>
        </div>
      </div>

      <div className="bg-white border border-[var(--line)] rounded-md p-6 md:p-8 space-y-5">
        <Field label="Titre">
          <input
            data-testid="article-title"
            value={form.title}
            onChange={(e) => update({ title: e.target.value })}
            placeholder="Ex : Les 80 ans du Festival de Cannes"
            className="w-full font-display text-3xl md:text-4xl border-b border-transparent focus:border-[var(--red)] outline-none py-2"
          />
        </Field>
        <Field label="Sous-titre">
          <input
            data-testid="article-subtitle"
            value={form.subtitle}
            onChange={(e) => update({ subtitle: e.target.value })}
            placeholder="Un chapô qui donne envie…"
            className="w-full text-lg text-neutral-700 border-b border-transparent focus:border-[var(--red)] outline-none py-2"
          />
        </Field>
        <Field label="Image de couverture (URL ou upload)">
          <MediaPicker
            value={form.cover_image_url}
            onChange={(url) => update({ cover_image_url: url })}
            accept="image/*"
            testId="article-cover"
          />
          {form.cover_image_url && (
            <img
              src={mediaUrl(form.cover_image_url)}
              alt="Couverture"
              className="mt-3 max-h-56 rounded border border-[var(--line)]"
            />
          )}
        </Field>
        <Field label="Auteur affiché">
          <input
            data-testid="article-author-display"
            list="author-display-suggestions"
            value={form.author_display}
            onChange={(e) => update({ author_display: e.target.value })}
            placeholder="Emmanuel"
            className="w-full py-2 border-b border-transparent focus:border-[var(--red)] outline-none"
          />
          <datalist id="author-display-suggestions">
            <option value="Emmanuel" />
            <option value="La Rédaction" />
          </datalist>
          <p className="mt-2 text-xs text-neutral-500">
            Choisissez « Emmanuel », « La Rédaction », ou saisissez librement un nom d'invité. Laissez vide pour utiliser le nom du compte connecté.
          </p>
        </Field>
        <Field label="Étiquette du teaser">
          {(() => {
            const hasVideo = form.blocks.some((b) => b?.type === "video");
            const autoLabel = hasVideo ? "À voir" : "À lire";
            return (
              <>
                <select
                  data-testid="article-teaser-label"
                  value={form.teaser_label || "auto"}
                  onChange={(e) => update({ teaser_label: e.target.value })}
                  className="w-full py-2 px-3 rounded border border-[var(--line-strong)] bg-white text-sm focus:border-[var(--red)] outline-none"
                >
                  <option value="auto">Automatique — {autoLabel} (selon le contenu)</option>
                  <option value="read">À lire</option>
                  <option value="watch">À voir</option>
                </select>
                <p className="mt-2 text-xs text-neutral-500">
                  Par défaut, l'étiquette affichée au-dessus du titre sur l'accueil et la page « Articles » est déduite automatiquement (« À voir » si l'article contient une vidéo, sinon « À lire »). Forcez-la ici si vous voulez la surcharger.
                </p>
              </>
            );
          })()}
        </Field>
        <Field label="Tags (séparés par une virgule)">
          <input
            data-testid="article-tags"
            value={form.tags.join(", ")}
            onChange={(e) => update({ tags: e.target.value.split(",").map((t) => t.trim()).filter(Boolean) })}
            placeholder="Cannes, Interview, Festival"
            className="w-full py-2 border-b border-transparent focus:border-[var(--red)] outline-none"
          />
        </Field>
        <Field label="Date de publication">
          <input
            type="datetime-local"
            data-testid="article-published-at"
            value={form.published_at || ""}
            onChange={(e) => update({ published_at: e.target.value })}
            className="w-full py-2 px-3 rounded border border-[var(--line-strong)] bg-white text-sm focus:border-[var(--red)] outline-none"
          />
          <div className="mt-2 flex items-center gap-3 text-xs text-neutral-500">
            <button
              type="button"
              data-testid="article-published-at-clear"
              onClick={() => update({ published_at: "" })}
              className="text-neutral-400 hover:text-[var(--red)] underline underline-offset-2"
            >
              Effacer
            </button>
            <button
              type="button"
              data-testid="article-published-at-now"
              onClick={() => update({ published_at: isoToLocalInput(new Date().toISOString()) })}
              className="text-neutral-400 hover:text-[var(--red)] underline underline-offset-2"
            >
              Maintenant
            </button>
          </div>
          <p className="mt-2 text-xs text-neutral-500">
            Laissez vide pour publier immédiatement au moment de l'enregistrement (comportement par défaut). Choisissez une date passée pour rétro-dater l'article, ou une date future pour <em>programmer</em> la publication : l'article sera enregistré comme <em>Publié</em> mais restera invisible du public jusqu'à cette date.
          </p>
        </Field>
        <Field label="Mise en avant sur l'accueil">
          <label
            data-testid="article-featured-toggle"
            className={`inline-flex items-center gap-3 mt-1 px-4 py-2.5 rounded-full border cursor-pointer select-none transition-colors ${
              form.is_featured
                ? "bg-[var(--red)] text-white border-[var(--red)]"
                : "bg-white text-neutral-700 border-[var(--line-strong)] hover:border-black"
            }`}
          >
            <input
              type="checkbox"
              checked={!!form.is_featured}
              onChange={(e) => update({ is_featured: e.target.checked })}
              className="sr-only"
            />
            <span className={`inline-block h-4 w-4 rounded-full border ${form.is_featured ? "bg-white border-white" : "border-neutral-400"}`}>
              {form.is_featured && <span className="block h-full w-full rounded-full bg-[var(--red)] scale-50" />}
            </span>
            <span className="text-[11px] uppercase tracking-[0.25em]">
              Épingler à « À la une »
            </span>
          </label>
          <p className="mt-2 text-xs text-neutral-500">
            Un seul article peut être épinglé à la fois. L'espace « Édito » sur l'accueil affiche toujours automatiquement votre dernier article tagué <em>Édito</em> — indépendamment de cette épingle.
          </p>
        </Field>
      </div>

      <div>
        <div className="text-[11px] uppercase tracking-[0.3em] text-neutral-500 mb-3">Contenu</div>
        <div className="space-y-4">
          {form.blocks.map((b, i) => (
            <BlockEditor
              key={i}
              index={i}
              block={b}
              onChange={(patch) => updateBlock(i, patch)}
              onRemove={() => removeBlock(i)}
              onMove={(dir) => moveBlock(i, dir)}
            />
          ))}
        </div>
        <div className="flex flex-wrap gap-2 mt-4">
          <ChipButton icon={Type} onClick={() => addBlock("paragraph")} label="Paragraphe" testId="add-paragraph" />
          <ChipButton icon={Heading2} onClick={() => addBlock("heading")} label="Titre" testId="add-heading" />
          <ChipButton icon={Quote} onClick={() => addBlock("quote")} label="Citation" testId="add-quote" />
          <ChipButton icon={ImageIcon} onClick={() => addBlock("image")} label="Photo" testId="add-image" />
          <ChipButton icon={Video} onClick={() => addBlock("video")} label="Vidéo" testId="add-video" />
        </div>
      </div>
    </div>
  );
};

const Field = ({ label, children }) => (
  <div>
    <div className="text-[10px] uppercase tracking-[0.3em] text-neutral-500 mb-1">{label}</div>
    {children}
  </div>
);

const ChipButton = ({ icon: Icon, onClick, label, testId }) => (
  <button
    data-testid={testId}
    onClick={onClick}
    className="inline-flex items-center gap-2 px-3.5 py-2 rounded-full border border-[var(--line-strong)] hover:border-black bg-white text-sm text-neutral-700 hover:text-black"
  >
    <Icon className="h-4 w-4" />
    {label}
  </button>
);

const BlockEditor = ({ index, block, onChange, onRemove, onMove }) => {
  const isTextish = block.type === "paragraph" || block.type === "heading" || block.type === "quote";
  return (
    <div className="bg-white border border-[var(--line)] rounded-md p-4">
      <div className="flex items-center justify-between mb-3">
        <div className="text-[10px] uppercase tracking-[0.25em] text-neutral-500">
          {index + 1} · {label(block.type)}
        </div>
        <div className="flex items-center gap-1">
          <IconBtn onClick={() => onMove(-1)} aria="Monter"><ChevronUp className="h-4 w-4" /></IconBtn>
          <IconBtn onClick={() => onMove(1)} aria="Descendre"><ChevronDown className="h-4 w-4" /></IconBtn>
          <IconBtn onClick={onRemove} aria="Supprimer" danger><Trash2 className="h-4 w-4" /></IconBtn>
        </div>
      </div>
      {isTextish && (
        <textarea
          data-testid={`block-${index}-text`}
          value={block.text || ""}
          onChange={(e) => onChange({ text: e.target.value })}
          rows={block.type === "paragraph" ? 4 : 2}
          placeholder={
            block.type === "heading" ? "Sous-titre de section…" :
            block.type === "quote" ? "Citation en italique…" :
            "Écrivez votre paragraphe ici…"
          }
          className={`w-full resize-y outline-none px-3 py-2 rounded border border-[var(--line)] focus:border-[var(--red)] ${
            block.type === "heading" ? "font-display text-2xl" :
            block.type === "quote" ? "font-display italic text-lg" :
            "text-neutral-800"
          }`}
        />
      )}
      {(block.type === "image" || block.type === "video") && (
        <div className="space-y-3">
          <MediaPicker
            value={block.url}
            onChange={(url, extra) => onChange({ url, ...(extra || {}) })}
            accept={block.type === "image" ? "image/*" : "video/*"}
            allowEmbed={block.type === "video"}
            testId={`block-${index}-media`}
          />
          {/* Explicit status pill so an editor never wonders whether a
              file is actually attached — critical after an upload that
              may have failed silently. */}
          <div
            className={`inline-flex items-center gap-2 text-[10px] uppercase tracking-widest rounded-full px-3 py-1 ${
              (block.url || "").trim()
                ? "bg-green-50 text-green-700 border border-green-200"
                : "bg-[var(--red)]/10 text-[var(--red)] border border-[var(--red)]/30"
            }`}
            data-testid={`block-${index}-media-status`}
          >
            {(block.url || "").trim()
              ? `${block.type === "video" ? "Vidéo" : "Photo"} attachée`
              : `Aucun fichier — ${block.type === "video" ? "uploadez une vidéo" : "uploadez une image"} ou collez un lien`}
          </div>
          {block.url && block.type === "image" && (
            <img
              src={mediaUrl(block.url)}
              alt=""
              className="max-h-72 rounded border border-[var(--line)]"
            />
          )}
          {block.url && block.type === "video" && !block.embed && (
            <video
              src={mediaUrl(block.url)}
              controls
              className="max-h-72 rounded border border-[var(--line)] bg-black w-full"
            />
          )}
          {block.url && block.type === "video" && block.embed && (
            <div className="text-xs text-neutral-500">
              Vidéo intégrée depuis <span className="text-neutral-800">{block.url}</span>
            </div>
          )}
          <input
            data-testid={`block-${index}-caption`}
            value={block.caption || ""}
            onChange={(e) => onChange({ caption: e.target.value })}
            placeholder="Légende (facultatif)"
            className="w-full text-sm px-3 py-2 rounded border border-[var(--line)] focus:border-[var(--red)] outline-none italic"
          />
        </div>
      )}
    </div>
  );
};

const IconBtn = ({ onClick, aria, children, danger }) => (
  <button
    onClick={onClick}
    aria-label={aria}
    className={`p-1.5 rounded hover:bg-neutral-100 ${
      danger ? "text-[var(--red)] hover:bg-red-50" : "text-neutral-500"
    }`}
  >
    {children}
  </button>
);

const label = (t) =>
  ({
    paragraph: "Paragraphe",
    heading: "Titre",
    quote: "Citation",
    image: "Photo",
    video: "Vidéo",
  }[t] || t);

const MediaPicker = ({ value, onChange, accept, allowEmbed = false, testId }) => {
  const inputRef = useRef();
  const [uploading, setUploading] = useState(false);
  const [urlInput, setUrlInput] = useState(value || "");

  useEffect(() => { setUrlInput(value || ""); }, [value]);

  const doUpload = async (file) => {
    if (!file) return;
    setUploading(true);
    try {
      const fd = new FormData();
      fd.append("file", file);
      const { data } = await api.post("/media/upload", fd, {
        headers: { "Content-Type": "multipart/form-data" },
        // Videos can be several hundred megabytes on slow connections
        // — the default 3-min timeout would abort silently, leaving
        // the user thinking the upload succeeded (spinner disappears
        // with no toast). 20 min covers realistic worst case.
        timeout: 20 * 60 * 1000,
      });
      onChange(data.url, { embed: false });
      setUrlInput(data.url);
      toast.success("Fichier envoyé");
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Upload échoué");
    } finally {
      setUploading(false);
    }
  };

  const applyUrl = () => {
    const v = urlInput.trim();
    if (!v) return;
    const isYT = /youtube\.com|youtu\.be/.test(v);
    const isVimeo = /vimeo\.com/.test(v);
    onChange(v, { embed: allowEmbed && (isYT || isVimeo) });
    toast.success("Lien pris en compte");
  };

  return (
    <div>
      <div className="flex flex-col md:flex-row gap-2">
        <div className="flex-1 flex items-center gap-2">
          <div className="flex-1 relative">
            <LinkIcon className="h-3.5 w-3.5 absolute left-3 top-1/2 -translate-y-1/2 text-neutral-400" />
            <input
              data-testid={`${testId}-url`}
              value={urlInput}
              onChange={(e) => setUrlInput(e.target.value)}
              placeholder={
                allowEmbed
                  ? "https://youtube.com/… ou lien direct .mp4"
                  : "URL de l'image (ou uploadez à droite)"
              }
              className="w-full pl-9 pr-3 py-2 rounded border border-[var(--line-strong)] text-sm focus:border-[var(--red)] outline-none"
            />
          </div>
          <button
            type="button"
            data-testid={`${testId}-apply-url`}
            onClick={applyUrl}
            className="text-xs px-3 py-2 rounded-full border border-[var(--line-strong)] hover:border-black"
          >
            Utiliser
          </button>
        </div>
        <button
          type="button"
          data-testid={`${testId}-upload`}
          onClick={() => inputRef.current?.click()}
          disabled={uploading}
          className="inline-flex items-center gap-2 text-xs px-3.5 py-2 rounded-full bg-[var(--red)] text-white hover:bg-[var(--red-dark)] disabled:opacity-60"
        >
          {uploading ? <Loader2 className="h-3.5 w-3.5 animate-spin" /> : <Upload className="h-3.5 w-3.5" />}
          {uploading ? "Envoi…" : "Uploader"}
        </button>
        <input
          ref={inputRef}
          type="file"
          accept={accept}
          hidden
          onChange={(e) => doUpload(e.target.files?.[0])}
        />
      </div>
    </div>
  );
};
